import React, {
  useState, Fragment, useEffect, useMemo,
  useRef,
} from 'react';
import { Link, withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import {
  Select, Table, DatePicker, Checkbox, Popconfirm, notification,
  Dropdown,
} from 'antd';
import { v4 as uuidv4 } from 'uuid';
import dayjs from 'dayjs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleCheck, faEraser, faRotateRight, faScissors, faTrashCan,
} from '@fortawesome/free-solid-svg-icons';
import H3Text from '@Uilib/h3Text';
import H3FormInput from '@Uilib/h3FormInput';
import H3Button, { defaultButtonTypes } from '@Uilib/h3Button';
import SelectSeller from '@Components/Common/SelectSeller';
import Helpers from '@Apis/helpers';
import TenantSelector from '@Components/Common/Selector/TenantSelector';
import SelectDepartment from '@Components/Common/SelectDepartment';
import SoActions from '@Actions/sales/soActions';
import SelectAppUser from '@Components/Common/SelectAppUser';
import Crown from '@Images/crown2.png';
import { QUANTITY } from '@Apis/constants';
import RestrictedAccessMessage from '../../../../Common/RestrictedAccess/RestrictedAccessMessage';
import './style.scss';
import PRZSelect from '../../../../Common/UI/PRZSelect';

const { Option } = Select;

const ConvertToMo = ({
  callback, selectedSO, user, convertSoToMoLoading, convertSoToMo, selectedRows, inputTenant, getSoAvailability, getSoAvailabilityLoading, isMrp, consolidateProductionItems, mrpId,
}) => {
  const [moLines, setMoLines] = useState([]);
  const [moLinesCopy, setMoLinesCopy] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [fgSelectedDepartment, setFgSelectedDepartment] = useState('');
  const [rmSelectedDepartment, setRmSelectedDepartment] = useState('');
  const [bpSelectedDepartment, setBpSelectedDepartment] = useState('');
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [scheduledDateCheckBox, setScheduledDateCheckBox] = useState(true);
  const [restrictMoUser, setRestrictMoUser] = useState(false);
  const [restrictionCheck, setRestrictionCheck] = useState(true);
  const [moUsers, setMoUsers] = useState([]);
  const [scheduledDate, setScheduledDate] = useState(dayjs());
  const [selectedProductRows, setSelectedProductRows] = useState([]);
  const [selectedProductRowKeys, setSelectedProductRowKeys] = useState([]);
  const [openTableAction, setOpenTableAction] = useState(false);
  const [moType, setMoType] = useState(false);
  const isExcessMoAllowed = user?.tenant_info?.production_config?.sub_modules?.manufacturing_order?.settings?.allow_manufacturing_order_more_than_sales_order;

  const filterRestrictedBom = (item) => {
    if ((item?.is_restricted && item?.bom_users.includes(user?.user_id)) || (!item?.is_restricted && item?.bom_users === null)) {
      return false;
    } return true;
  };

  const bomUserPermission = (soLines) => {
    let restrictAccess = [];

    for (let i = 0; i < soLines?.length; i++) {
      const soLine = soLines[i];
      if (soLine?.bom_list?.filter((item) => !filterRestrictedBom(item))?.length) {
        const selectedBom = soLine?.bom_list?.filter((item) => !filterRestrictedBom(item))?.find((item) => item?.is_active) || soLine?.bom_list?.filter((item) => !filterRestrictedBom(item))[0];
        if (selectedBom?.bom_users === null || selectedBom?.bom_users.includes(user?.user_id)) {
          if (selectedBom?.bom_users?.length) {
            restrictAccess = [...restrictAccess, ...selectedBom?.bom_users];
          }
        }
      }
    }
    setMoUsers(restrictAccess);
  };

  const allowAdhocNonBomFgInMo = useMemo(() => user?.tenant_info?.production_config?.sub_modules?.manufacturing_order?.settings?.allow_adhoc_non_bom_fg_in_mo, [user]);
  const accessRestriction = useMemo(() => user?.tenant_info?.production_config?.addons?.access_restriction?.is_active, [user]);
  const manufacturingOrder = useMemo(() => user?.tenant_info?.production_config?.sub_modules?.manufacturing_order?.is_active, [user]);

  useEffect(() => {
    // Set initial state for removed keys in sessionStorage to reset the values if already exists.
    sessionStorage.setItem('removed_line_keys_so_to_mo', JSON.stringify([]));
    setMoType(user?.tenant_info?.sales_config?.sub_modules.sales_order.settings.generate_multiple_mo_from_so
    )
  }, []);

  useEffect(() => {
    if ((selectedRows || selectedSO)) {
      const tenantId = selectedTenant || selectedSO?.tenant_id || inputTenant;
      const tdId = selectedDepartment || user?.user_tenants?.find((item) => item?.tenant_id === tenantId)?.default_department_for_fg;
      const soIds = selectedSO ? [selectedSO?.order_id] : selectedRows?.map((item) => item?.order_id);
      const soLineIds = consolidateProductionItems ? selectedRows?.map((item) => item?.lines?.map((line) => line?.order_line_id))?.flat() : selectedRows?.map((item) => item?.order_line_id);
      setSelectedTenant(tenantId);
      setSelectedDepartment(tdId);
      setFgSelectedDepartment(user?.user_tenants?.find((item) => item?.tenant_id === tenantId)?.default_department_for_fg);
      setRmSelectedDepartment(user?.user_tenants?.find((item) => item?.tenant_id === tenantId)?.default_department_for_rm);
      setBpSelectedDepartment(user?.user_tenants?.find((item) => item?.tenant_id === tenantId)?.default_department_for_bp);
      const soLinesPayload = {
        soIds: isMrp ? null : soIds?.join(','),
        tenantId,
        tenantDepartmentId: isMrp ? null : tdId,
        soLineIds: isMrp ? soLineIds : null,
        mrpId: isMrp ? mrpId : null,
      };
      getSoAvailability(soLinesPayload, (soLines) => {
        const lines = [];
        const noBom = [];
        for (let i = 0; i < soLines?.length; i++) {
          const soLine = soLines[i];
          const qty = ((Number(soLine?.so_quantity) + Number(soLine?.so_free_quantity || '0')) * soLine?.so_uom_info?.[0]?.ratio) / soLine?.product_uom_info?.ratio;
          const tempLine = {
            key: uuidv4(),
            scheduledDate,
            deliveryDate: dayjs(scheduledDate).isBefore(soLine?.delivery_date) ? soLine?.delivery_date : scheduledDate,
            tenantDepartmentId: selectedDepartment,
            fgTenantProductId: soLine?.mo_tp_id,
            quantity: isMrp ? soLine?.pending_quantity : (qty - Number(soLine?.total_fg_to_be_produced)) > 0 ? (qty - Number(soLine?.total_fg_to_be_produced)) : 0,
            soQuantity: isMrp ? soLine?.demand_quantity : qty,
            uomId: soLine?.product_uom_id,
            uomInfo: soLine?.product_uom_info,
            sentForProduction: soLine?.total_fg_to_be_produced,
            actualFgQuantity: soLine?.actual_fg_produced,
            availableQuantity: soLine?.unreserved_quantity > 0 ? soLine?.unreserved_quantity : 0,
            soLineId: soLine?.order_line_id,
            soId: soLine?.order_id,
            orderDate: soLine?.order_date,
            soNumber: soLine?.sales_order_number,
            productSkuName: soLine?.product_sku_name,
            productSkuId: soLine?.product_sku_id,
            refProductCode: soLine?.ref_product_code,
            internalSkuCode: soLine?.internal_sku_code,
            tenantName: soLine?.so_tenant_name,
            job_works_type: 'batch_level',
            mrp_id: soLine?.mrp_id,
            remarks: soLine?.remarks,
          };
          if (soLine?.bom_list?.filter((item) => !filterRestrictedBom(item))?.length) {
            const selectedBom = soLine?.bom_list?.filter((item) => !filterRestrictedBom(item))?.find((item) => item?.is_active) || soLine?.bom_list?.filter((item) => !filterRestrictedBom(item))[0];
            lines.push({
              ...tempLine,
              bomList: soLine?.bom_list?.filter((item) => !filterRestrictedBom(item)),
              selectedBom: selectedBom?.bom_id,
              enableWastageTracking: selectedBom?.enable_wastage_tracking ? 'YES' : 'NO',
            });
            setRestrictMoUser(accessRestriction ? !!((restrictionCheck && selectedBom?.bom_users?.length > 0)) : false);
          } else {
            noBom.push({
              ...tempLine,
              bomList: null,
              selectedBom: null,
              enableWastageTracking: 'NO',
            });
          }
        }
        bomUserPermission(soLines);
        setMoLines([...lines, ...noBom]);
        setMoLinesCopy([...lines, ...noBom]);
      });
    }
  }, [selectedSO, selectedDepartment]);

  const updateData = (key, label, value) => {
    const tempMoLines = JSON.parse(JSON.stringify(moLines));
    for (let i = 0; i < tempMoLines?.length; i++) {
      if (tempMoLines[i]?.key === key) {
        tempMoLines[i][label] = value;
        break;
      }
    }
    setMoLines(tempMoLines);
  };

  const isDataValid = () => {
    const erroredLines = moLines.filter((line) => line.quantity > (line?.soQuantity - line?.sentForProduction));
    let _isDataValid = true;
    _isDataValid = !(erroredLines.length > 0);
    return _isDataValid;
  };

  const onSubmit = () => {
    setFormSubmitted(true);
    const fgList = [];
    const tempMoLines = allowAdhocNonBomFgInMo ? moLines : moLines?.filter((item) => item?.bomList);
    for (let i = 0; i < tempMoLines?.length; i++) {
      fgList.push({
        tenant_id: selectedTenant,
        is_restricted: restrictMoUser,
        mo_users: restrictMoUser ? moUsers : null,
        org_id: user?.tenant_info?.org_id,
        bom_id: tempMoLines[i]?.selectedBom,
        scheduled_date: tempMoLines[i]?.scheduledDate,
        delivery_date: tempMoLines[i]?.deliveryDate,
        enable_wastage_tracking: tempMoLines[i]?.enableWastageTracking === 'YES',
        job_works_type: tempMoLines[i]?.job_works_type,
        tenant_department_id: selectedDepartment,
        fg_tenant_department_id: Number(fgSelectedDepartment),
        rm_tenant_department_id: Number(rmSelectedDepartment),
        bp_tenant_department_id: Number(bpSelectedDepartment),
        tenant_seller_id: tempMoLines[i]?.tenantSellerId,
        seller_name: tempMoLines[i]?.sellerName,
        seller_address: tempMoLines[i]?.sellerAddress,
        seller_gst: tempMoLines[i]?.sellerGst,
        so_line_ids: Array.isArray(tempMoLines[i]?.soLineId) ? tempMoLines[i]?.soLineId : [tempMoLines[i]?.soLineId],
        fg_tenant_product_id: tempMoLines[i]?.fgTenantProductId,
        fg_product_sku_id: tempMoLines[i]?.productSkuId,
        quantity: tempMoLines[i]?.quantity,
        uom_id: tempMoLines[i]?.uomId,
        uom_info: tempMoLines[i]?.uomInfo,
        mrp_id: tempMoLines[i]?.mrp_id,
      });
    }
    if (fgList?.find((item) => Number(item?.quantity) <= 0)) {
      notification.open({
        message: 'Please enter a valid quantity for creating manufacturing orders',
        duration: 4,
        placement: 'top',
        type: 'warning',
      });
    } else if (!isExcessMoAllowed && !isDataValid()) {
      notification.open({
        message: 'Manufacturing quantity cannot exceed sales quantity.',
        duration: 4,
        placement: 'top',
        type: 'error',
      });
    } else {
      const payload = {
        mo_type: moType ? 'MULTIPLE' : 'SINGLE',
        fg_list: fgList,
      };
      convertSoToMo(payload, () => {
        callback();
        setFormSubmitted(false);
      });
    }
  };

  const getTableColumns = () => {
    const columns = [
      {
        title: 'PRODUCT',
        width: '200px',
        responsive: ['sm', 'md', 'lg', 'xxl'],
        render: (item) => (
          <Fragment>
            <Link to={`/inventory/product/view/${item?.productSkuId}?tab=1`} target="_blank">
              <H3Text text={`#${item?.internalSkuCode}`} />
            </Link>
            {item?.productSkuName}
            {!consolidateProductionItems && (
              <div className="so-mo-table__so-info">
                <Link to={`/sales/sales-order/view/${item?.soId}?tab=1`} target="_blank">
                  <H3Text text={`#${item?.soNumber}`} />
                </Link>
                <H3Text text={item?.tenantName} />
              </div>
            )}
            <div
              dangerouslySetInnerHTML={{ __html: item?.remarks }}
              className="table-subscript-grey"
              style={{
                whiteSpace: 'pre-wrap',
                marginTop: '5px',
              }}
            />
          </Fragment>
        ),
      },
      {
        title: 'TO BE PRODUCED',
        responsive: ['sm', 'md', 'lg', 'xxl'],
        width: '140px',
        render: (record) => (
          <div>
            {
              (record?.bomList?.length || allowAdhocNonBomFgInMo) ? (
                <Fragment>
                  <div className="table-input-v2__wrapper table-input-v2__wrapper-long">
                    <H3Text text="Available Stock" className="table-input-v2__input-label" />
                    <H3Text text={`${QUANTITY(record?.availableQuantity, record?.uomInfo?.precision)} ${record?.uomInfo?.uqc?.toProperCase()}`} className="table-input-v2__input-value" />
                  </div>
                  <div className="table-input-v2__wrapper table-input-v2__wrapper-long">
                    <H3Text text="To Be Produced" className="table-input-v2__input-label" />
                    <H3Text text={`${QUANTITY(record?.soQuantity, record?.uomInfo?.precision)} ${record?.uomInfo?.uqc?.toProperCase()}`} className="table-input-v2__input-value" />
                  </div>
                  <div className="table-input-v2__wrapper table-input-v2__wrapper-long">
                    <H3Text text="Sent for Production" className="table-input-v2__input-label" />
                    <H3Text
                      text={(
                        <div>
                          {`${QUANTITY(record?.sentForProduction, record?.uomInfo?.precision)} ${record?.uomInfo?.uqc?.toProperCase()}`}
                          {Number(record?.sentForProduction) >= Number(record?.soQuantity) && <FontAwesomeIcon icon={faCircleCheck} style={{ marginLeft: '10px', color: 'green' }} />}
                        </div>
                      )}
                      className="table-input-v2__input-value"
                    />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div>
                      <H3FormInput
                        value={record.quantity}
                        name="quantity less than pending quantity"
                        type="number"
                        label="Quantity"
                        containerClassName={`table-input-v2__wrapper table-input-v2__wrapper-long table-input-v2__wrapper-flexible ${!isExcessMoAllowed && (record?.soQuantity - record?.sentForProduction) < Number(record?.quantity) ? 'show-error' : ''}`}
                        labelClassName="table-input-v2__input-label"
                        inputClassName="orgFormInput"
                        onChange={(e) => updateData(record?.key, 'quantity', e.target.value)}
                        // showError={!isExcessMoAllowed && (record?.soQuantity - record?.sentForProduction) < Number(record?.quantity)}
                        disabled={!isExcessMoAllowed && (record?.soQuantity - record?.sentForProduction) <= 0}
                      />
                    </div>
                    <div style={{ marginLeft: '5px' }}>
                      {record?.uomInfo?.uqc?.toProperCase()}
                    </div>
                  </div>
                  <div className="table-input-v2__wrapper">
                    <Checkbox
                      checked={record?.enableWastageTracking === 'YES'}
                      onChange={() => {
                        const value = record?.enableWastageTracking === 'YES' ? 'NO' : 'YES';
                        updateData(record?.key, 'enableWastageTracking', value);
                      }}
                    >
                      Enable Wastage Tracking
                    </Checkbox>
                  </div>
                </Fragment>
              ) : ''
            }
          </div>
        ),
      },
      {
        title: 'PRODUCTION DETAILS',
        responsive: ['sm', 'md', 'lg', 'xxl'],
        render: (record) => (
          <div>
            {
              (record?.bomList?.length || allowAdhocNonBomFgInMo) ? (
                <Fragment>
                  <div className={`table-input-v2__wrapper ${!record?.bomList?.length ? 'so-mo-table__disable_no_bom_item' : ''}`}>
                    <H3Text text="Bill of Material" className="table-input-v2__input-label" />
                    &nbsp;
                    <PRZSelect
                      className={`${!record?.bomList?.length ? 'so-mo-table__disable_no_bom_item' : ''}`}
                      value={record?.selectedBom}
                      filterOption={false}
                      // getPopupContainer={(triggerNode) => triggerNode.parentNode}
                      onChange={(value) => {
                        const tempMoLines = JSON.parse(JSON.stringify(moLines));
                        for (let i = 0; i < tempMoLines?.length; i++) {
                          if (tempMoLines[i]?.key === record?.key) {
                            tempMoLines[i].selectedBom = value;
                            tempMoLines[i].enableWastageTracking = record?.bomList?.find((item) => item?.bom_id == value)?.enable_wastage_tracking ? 'YES' : 'NO';
                            break;
                          }
                        }
                        setMoUsers([...new Set([...moUsers, ...(value?.bom_users?.length > 0 ? value.bom_users : [])])]);
                        setRestrictionCheck(!!((restrictionCheck && value?.bom_users?.length > 0)));
                        setMoLines(tempMoLines);
                      }}
                      disabled={!record?.bomList?.length}
                      placeholder={record?.bomList?.length ? 'Select BOM' : 'BOM not available'}
                    >
                      {
                        record?.bomList?.map((item) => (
                          <Option key={item?.bom_id} value={item?.bom_id}>
                            {`${item?.bom_number} -  ${item?.bom_alias_name}`}
                          </Option>
                        ))
                      }
                    </PRZSelect>
                  </div>
                  <div className="table-input-v2__wrapper">
                    <H3Text text="Scheduled Date" className="table-input-v2__input-label" />
                    &nbsp;
                    <DatePicker
                      value={dayjs(record?.scheduledDate)}
                      onChange={(value) => {
                        updateData(record?.key, 'scheduledDate', value);
                      }}
                      format="DD-MMM-YYYY"
                      style={{
                        border: '1px solid rgba(68, 130, 218, 0.2)',
                        borderRadius: '2px',
                        height: '28px',
                        padding: '1px 3px',
                        background: 'white',
                      }}
                      allowClear={false}
                      disabled={scheduledDateCheckBox}
                    />
                  </div>
                  <div className="table-input-v2__wrapper">
                    <H3Text text="Completion Date" className="table-input-v2__input-label" />
                    &nbsp;
                    <DatePicker
                      value={dayjs(record?.deliveryDate)}
                      onChange={(value) => {
                        updateData(record?.key, 'deliveryDate', value);
                      }}
                      format="DD-MMM-YYYY"
                      style={{
                        border: '1px solid rgba(68, 130, 218, 0.2)',
                        borderRadius: '2px',
                        height: '28px',
                        padding: '1px 3px',
                        background: 'white',
                      }}
                      allowClear={false}
                      disabledDate={(current) => current < dayjs().startOf('day')}
                    />
                  </div>
                  <div className="table-input-v2__wrapper">
                    <H3Text text="Subcontractor" className="table-input-v2__input-label" />
                    &nbsp;
                    <SelectSeller
                      onChange={(value) => {
                        const tempMoLines = JSON.parse(JSON.stringify(moLines));
                        for (let i = 0; i < tempMoLines?.length; i++) {
                          if (tempMoLines[i]?.key === record?.key) {
                            tempMoLines[i].tenantSellerId = value?.tenant_seller_id;
                            tempMoLines[i].sellerName = value?.seller_info?.seller_name;
                            tempMoLines[i].sellerAddress = value?.seller_info?.office_address_details;
                            tempMoLines[i].sellerGst = value?.seller_info?.gst_number;
                            break;
                          }
                        }
                        setMoLines(tempMoLines);
                      }}
                      inputClassName="XXXDONOTREMOVE"
                      selectedSeller={record?.tenantSellerId}
                      tenantId={selectedTenant}
                      showAddVendor={false}
                      notRequired
                      isSubcontractor
                      hideTitle
                      customStyle={{
                        border: 'none !important',
                      }}
                    />
                  </div>
                </Fragment>
              ) : <H3Text text="BOM NOT AVAILABLE" className="so-mo-table__no-info" />
            }
          </div>
        ),
      },
      {
        title: '',
        responsive: ['sm', 'md', 'lg', 'xxl'],
        render: (record) => (
          <div>
            {
              (allowAdhocNonBomFgInMo || record?.bomList) ? (
                <Popconfirm
                  title="Are you sure you want to remove this item?"
                  onConfirm={() => {
                    setMoLines(moLines?.filter((item) => item?.key !== record?.key));
                  }}
                >
                  <div className="delete-line-button__new">
                    <FontAwesomeIcon icon={faTrashCan} />
                  </div>
                </Popconfirm>
              ) : (
                <div className="delete-line-button__new" onClick={() => setMoLines(moLines?.filter((item) => item?.key !== record?.key))}>
                  <FontAwesomeIcon icon={faTrashCan} />
                </div>
              )
            }
          </div>
        ),
      },
    ];
    return columns;
  };

  const handleResetMoLines = () => {
    const copyData = JSON.parse(JSON.stringify(moLinesCopy));
    sessionStorage.setItem('removed_line_keys_so_to_mo', JSON.stringify([]));
    setMoLines(copyData);
    setSelectedProductRowKeys([]);
    setSelectedProductRows([]);
    setOpenTableAction(false);
    notification.open({
      type: 'success',
      message: 'All Lines has been restored successfully.',
      placement: 'top',
      duration: 5,
    });
  };

  const handleRemoveMoLines = (selectedRowKeys) => {
    const copyData = JSON.parse(JSON.stringify(moLinesCopy));

    // Retrieve previously removed keys from sessionStorage (this will reset on page refresh or close)
    const storedRemovedKeys = JSON.parse(sessionStorage.getItem('removed_line_keys_so_to_mo')) || [];

    // Merge current and previously removed keys
    const updatedRemovedKeys = [...new Set([...storedRemovedKeys, ...selectedRowKeys])];

    // Store updated removed keys in sessionStorage
    sessionStorage.setItem('removed_line_keys_so_to_mo', JSON.stringify(updatedRemovedKeys));

    // Filter out the removed lines from the data
    const filteredData = copyData?.filter((item) => !updatedRemovedKeys?.includes(item.key));

    // Show notifications for removed lines
    const removedLines = copyData?.filter((item) => selectedRowKeys?.includes(item.key));

    for (let index = 0; index < removedLines.length; index++) {
      const line = removedLines[index];
      const productName = line?.productSkuName || '';
      const skuCode = line?.internalSkuCode || '';
      notification.open({
        type: 'success',
        message: `The product "${skuCode}: ${productName}" has been removed.`,
        placement: 'top',
        duration: 5,
      });
    }

    setMoLines(filteredData);
    setSelectedProductRowKeys([]);
    setSelectedProductRows([]);
    setOpenTableAction(false);
  };

  const handleRemoveAllNonBomMoLines = () => {
    const copyData = JSON.parse(JSON.stringify(moLinesCopy));

    const filterNonBomLineKeys = copyData.filter((data) => !data?.bomList?.length)?.map((data) => data?.key);

    // Retrieve previously removed keys from sessionStorage (this will reset on page refresh or close)
    const storedRemovedKeys = JSON.parse(sessionStorage.getItem('removed_line_keys_so_to_mo')) || [];

    // Merge current and previously removed keys
    const updatedRemovedKeys = [...new Set([...storedRemovedKeys, ...filterNonBomLineKeys])];

    // Store updated removed keys in sessionStorage
    sessionStorage.setItem('removed_line_keys_so_to_mo', JSON.stringify(updatedRemovedKeys));

    // Filter out the removed lines from the data
    const filteredData = copyData?.filter((item) => !updatedRemovedKeys?.includes(item.key));

    // Show notifications for removed lines
    const removedLines = copyData?.filter((item) => filterNonBomLineKeys?.includes(item.key));

    for (let index = 0; index < removedLines.length; index++) {
      const line = removedLines[index];
      const productName = line?.productSkuName || '';
      const skuCode = line?.internalSkuCode || '';
      notification.open({
        type: 'success',
        message: `The product "${skuCode}: ${productName}" has been removed.`,
        placement: 'top',
        duration: 5,
      });
    }

    setMoLines(filteredData);
    setSelectedProductRowKeys([]);
    setSelectedProductRows([]);
    setOpenTableAction(false);
  };

  return (
    <Fragment>
      <div className="create-so-mo__wrapper" style={{ marginBottom: '40px' }}>
        <div className="ant-row">
          <div className="ant-col-md-8">
            <div className="create-so-mo__row">
              <H3Text required text="Select Location to Create Manufacturing Order" className="create-so-mo__row-label" />
              <TenantSelector
                selectedTenant={selectedTenant}
                showSearch
                onChange={(value) => {
                  setSelectedTenant(value);
                  setSelectedDepartment(user?.user_tenants?.find((item) => item?.tenant_id == value)?.default_department_for_production);
                  setFgSelectedDepartment(user?.user_tenants?.find((item) => item?.tenant_id === value)?.default_department_for_fg);
                  setRmSelectedDepartment(user?.user_tenants?.find((item) => item?.tenant_id === value)?.default_department_for_rm);
                  setBpSelectedDepartment(user?.user_tenants?.find((item) => item?.tenant_id === value)?.default_department_for_bp);
                }}
                placeholder="Select Business Unit"
                inputClassName="orgInputContainer"
                customStyle={{
                  borderRadius: '4px',
                  width: '98%',
                  fontSize: '13px',
                }}
                hideTitle
                disabled={convertSoToMoLoading || getSoAvailabilityLoading || isMrp}
                loading={getSoAvailabilityLoading}
                includedTenants={Helpers.getTenantEntityPermission(user?.user_tenants, Helpers.permissionEntities.MO, Helpers.permissionTypes.READ)}
              />
            </div>
          </div>
          <div className="ant-col-md-8">
            <div className="create-so-mo__row">
              <H3Text required text="Department for Production" className="create-so-mo__row-label" />
              <SelectDepartment
                hideTitle
                tenantId={isMrp ? inputTenant : selectedTenant}
                tenentLevelDepartment
                selectedDepartment={Number(selectedDepartment) || ''}
                noDropdownAlign
                onChange={(value) => {
                  setSelectedDepartment(value?.tenant_department_id.toString() || '');
                }}
                emptyNotAllowed
                disabled={convertSoToMoLoading || getSoAvailabilityLoading}
                customStyle={{
                  borderRadius: '4px',
                  width: '98%',
                  fontSize: '13px',
                  marginBottom: '10px',
                }}
                loading={getSoAvailabilityLoading}
              />
            </div>
          </div>
          <div className="ant-col-md-8">
            <div className="create-so-mo__row">
              <H3Text required text="Department for Raw Materials" className="create-so-mo__row-label" />
              <SelectDepartment
                hideTitle
                tenantId={isMrp ? inputTenant : selectedTenant}
                tenentLevelDepartment
                selectedDepartment={Number(rmSelectedDepartment) || ''}
                noDropdownAlign
                onChange={(value) => {
                  setRmSelectedDepartment(value?.tenant_department_id.toString() || '');
                }}
                emptyNotAllowed
                disabled={convertSoToMoLoading || getSoAvailabilityLoading}
                customStyle={{
                  borderRadius: '4px',
                  width: '98%',
                  fontSize: '13px',
                  marginBottom: '10px',
                }}
                loading={getSoAvailabilityLoading}
              />
            </div>
          </div>
          <div className="ant-col-md-8">
            <div className="create-so-mo__row">
              <H3Text required text="Department for Finished Goods" className="create-so-mo__row-label" />
              <SelectDepartment
                hideTitle
                tenantId={isMrp ? inputTenant : selectedTenant}
                tenentLevelDepartment
                selectedDepartment={Number(fgSelectedDepartment) || ''}
                noDropdownAlign
                onChange={(value) => {
                  setFgSelectedDepartment(value?.tenant_department_id.toString() || '');
                }}
                emptyNotAllowed
                disabled={convertSoToMoLoading || getSoAvailabilityLoading}
                customStyle={{
                  borderRadius: '4px',
                  width: '98%',
                  fontSize: '13px',
                  marginBottom: '10px',
                }}
                loading={getSoAvailabilityLoading}
              />
            </div>
          </div>
          <div className="ant-col-md-8">
            <div className="create-so-mo__row">
              <H3Text required text="Department for By Products" className="create-so-mo__row-label" />
              <SelectDepartment
                hideTitle
                tenantId={isMrp ? inputTenant : selectedTenant}
                tenentLevelDepartment
                selectedDepartment={Number(bpSelectedDepartment) || ''}
                noDropdownAlign
                onChange={(value) => {
                  setBpSelectedDepartment(value?.tenant_department_id.toString() || '');
                }}
                emptyNotAllowed
                disabled={convertSoToMoLoading || getSoAvailabilityLoading}
                customStyle={{
                  borderRadius: '4px',
                  width: '98%',
                  fontSize: '13px',
                  marginBottom: '10px',
                }}
                loading={getSoAvailabilityLoading}
              />
            </div>
          </div>
          <div className="ant-col-md-8" />
          <div className="ant-col-md-8">
            <div className="create-so-mo__row">
              <Checkbox
                checked={scheduledDateCheckBox}
                onChange={(e) => {
                  setScheduledDateCheckBox(e.target.checked);
                }}
                disabled={convertSoToMoLoading || getSoAvailabilityLoading}
                style={{ margin: '-5px 0px 2px 0px' }}
              >
                <H3Text text="Use Same Scheduled Date for All Items" className="create-so-mo__row-label" />
              </Checkbox>
              {scheduledDateCheckBox && (
                <React.Fragment>
                  <DatePicker
                    value={dayjs(scheduledDate)}
                    format="DD-MMM-YYYY"
                    onChange={(date, dateString) => {
                      // Handle the selected date as needed
                      setScheduledDate(dateString);
                      // update the key in the moLines
                      const tempMoLines = JSON.parse(JSON.stringify(moLines));
                      for (let i = 0; i < tempMoLines?.length; i++) {
                        tempMoLines[i].scheduledDate = dateString;
                      }
                      setMoLines(tempMoLines);
                    }}
                    style={{
                      border: '1px solid rgba(68, 130, 218, 0.2)',
                      borderRadius: '4px',
                      height: '28px',
                      width: '98%',
                      background: 'white',
                      margin: '0px 0px 10px 0px',
                    }}
                  />
                </React.Fragment>
              )}
            </div>
          </div>
          <div className="ant-col-md-8">
            <div className="create-so-mo__row">
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <Checkbox
                  checked={restrictMoUser}
                  onChange={(e) => {
                    setRestrictMoUser(e.target.checked);
                    setRestrictionCheck(e.target.checked !== false);
                  }}
                  disabled={convertSoToMoLoading || getSoAvailabilityLoading || !accessRestriction}
                  style={{ margin: '-5px 0px 2px 0px' }}
                >
                  <H3Text text="Restrict Access" className="create-so-mo__row-label" />
                </Checkbox>
                {!accessRestriction && (
                  <Popconfirm
                    placement="topRight"
                    title="This feature is not available in your current plan"
                    onConfirm={() => window.Intercom('showNewMessage')}
                    okText="Contact Us"
                    cancelText="Cancel"
                  >
                    <img className="create-so-mo__row-premium-img" src={Crown} alt="premium" />
                  </Popconfirm>
                )}
              </div>
              {restrictMoUser && (
                <Fragment>
                  <div style={{ marginBottom: '12px' }}>
                    <div className="mo-form__input-row__input">
                      <SelectAppUser
                        hideTitle
                        labelClassName="orgFormLabel"
                        inputClassName="orgFormInput"
                        selectedUser={moUsers}
                        onChange={(value) => setMoUsers(value)}
                        disabled={convertSoToMoLoading || getSoAvailabilityLoading}
                        isMultiple
                        placeholder="Select authorized users.."
                        allUsers
                        customStyle={{
                          borderRadius: '4px',
                          width: '98%',
                          fontSize: '13px',
                          marginBottom: '10px',
                        }}
                      />
                      {(formSubmitted && !(moUsers?.length > 0)) && (
                        <div className="input-error">
                          *Please select manufacturing order users
                        </div>
                      )}
                    </div>
                  </div>
                </Fragment>
              )}
            </div>
          </div>
        </div>
        <div className="create-so-mo__row">
          <Checkbox
            checked={moType}
            onChange={(e) => {
              setMoType(e.target.checked);
            }}
            disabled={convertSoToMoLoading || getSoAvailabilityLoading}
            style={{ margin: '-5px 0px 2px 0px' }}
          >
            <H3Text text="Create Multiple Manufacturing Orders" className="create-so-mo__row-label" />
          </Checkbox>
        </div>
        <Table
          bordered
          showHeader
          title={() => (
            <div className="create-mo-table-title__wrapper">
              {`Items to be Produced(${moLines?.length})`}
              <div className="create-mo-table-title-left">
                <Dropdown
                  open={openTableAction}
                  onOpenChange={() => {
                    if (openTableAction) {
                      setOpenTableAction(false);
                    }
                  }}
                  getPopupContainer={(triggerNode) => triggerNode.parentNode}
                  overlay={(
                    <div className="line_actions-wrapper">
                      <div className="line_actions" onClick={handleResetMoLines}>
                        <FontAwesomeIcon icon={faRotateRight} style={{ marginTop: '6px' }} size="sm" />
                        &nbsp; Reset MO Lines
                      </div>
                      {selectedProductRowKeys?.length > 0 && (
                        <div className="line_actions" onClick={() => handleRemoveMoLines(selectedProductRowKeys)}>
                          <FontAwesomeIcon icon={faScissors} style={{ marginTop: '6px' }} size="sm" />
                          &nbsp; Remove Selected Lines
                        </div>
                      )}
                      {moLines.filter((line) => !line?.bomList?.length).length > 0 && (
                        <div className="line_actions" onClick={handleRemoveAllNonBomMoLines}>
                          <FontAwesomeIcon icon={faEraser} style={{ marginTop: '6px' }} size="sm" />
                          &nbsp; Remove All Non BOM Lines
                        </div>
                      )}
                    </div>
                  )}
                  placement="topRight"
                  trigger={['click']}
                >
                  <div style={{ marginRight: '5px' }} className="custom-doc-columns__wrapper" onClick={() => setOpenTableAction(true)}>
                    Actions
                  </div>
                </Dropdown>
              </div>
            </div>
          )}
          size="small"
          columns={getTableColumns()?.filter((i) => !i?.hidden)}
          dataSource={moLines}
          pagination={false}
          loading={convertSoToMoLoading || getSoAvailabilityLoading}
          scroll={{ x: 'max-content' }}
          rowClassName={(record) => (!allowAdhocNonBomFgInMo && !record?.bomList?.length ? 'so-mo-table__disabled-row' : '')}
          rowSelection={(window.screen.width > 325) ? {
            onChange: (rowKeys, rows) => {
              setSelectedProductRowKeys(rowKeys);
              setSelectedProductRows(rows);
            },
            selectedRowKeys: selectedProductRowKeys,
          } : false}
        />
        <div className="custom-drawer__footer" style={{ display: 'flex', width: '975px' }}>
          <H3Button
            buttonType={defaultButtonTypes.BLUE_ROUNDED}
            text="Submit"
            onClick={() => onSubmit()}
            isLoading={convertSoToMoLoading}
            disabled={convertSoToMoLoading || getSoAvailabilityLoading || !manufacturingOrder}
            style={{
              width: '160px',
              borderRadius: '5px',
              padding: '7px 0px',
              marginLeft: '0',
            }}
          />
          {manufacturingOrder && !allowAdhocNonBomFgInMo && <RestrictedAccessMessage message="Manufacturing Order will not be created for non-BOM Lines" />}
          {!manufacturingOrder && (
            <Popconfirm
              placement="topRight"
              title="This feature is not available in your current plan"
              onConfirm={() => window.Intercom('showNewMessage')}
              okText="Contact Us"
              cancelText="Cancel"
            >
              <img
                className="barcode-restrict"
                src={Crown}
                alt="premium"
                style={{
                  marginLeft: '8px',
                  marginTop: '5px',
                }}
              />
            </Popconfirm>
          )}
        </div>
      </div>
    </Fragment>
  );
};

const mapStateToProps = ({
  UserReducers, SOReducers,
}) => ({
  user: UserReducers.user,
  convertSoToMoLoading: SOReducers.convertSoToMoLoading,
  getSoAvailabilityLoading: SOReducers.getSoAvailabilityLoading,
});

const mapDispatchToProps = (dispatch) => ({
  convertSoToMo: (payload, callback) => dispatch(SoActions.convertSoToMo(payload, callback)),
  getSoAvailability: (payload, callback) => dispatch(SoActions.getSoAvailability(payload, callback)),
});

ConvertToMo.propTypes = {
  selectedSO: PropTypes.any,
  callback: PropTypes.any,
  user: PropTypes.any,
  convertSoToMoLoading: PropTypes.bool,
  convertSoToMo: PropTypes.func,
  selectedRows: PropTypes.array,
  inputTenant: PropTypes.any,
  getSoAvailability: PropTypes.func,
  getSoAvailabilityLoading: PropTypes.bool,
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(ConvertToMo));
